import "react-resizable/css/styles.css"
import "./Sidebar.scss"

import React, { useCallback, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"

import settings from "../../../constants/settings"
import { useFlag } from "../../../context/UnleashContext"
import * as appActions from "../../../redux/actions"
import config from "../../../utils/config"
import { useGlobalTranslation } from "../../../utils/hook"
import { hasPermission, PERMISSIONS } from "../../../utils/team-management/team-management-utils"
import {
  hasWhitelabelPermission,
  hideBilling,
  isAdmin,
  isNotSkylead,
  WHITELABEL_PERMISSIONS,
} from "../../../utils/whitelabel-utils"
import { withRouter } from "../../../utils/with-router"
import Navigation, { NavigationVariant } from "../Navigation"

const secureRegistrationFeatureFlag = config.REACT_APP_SECURED_REGISTRATION

const Sidebar = ({
  navbarWidth,
  setNavbarWidth = () => {},
  navType = NavigationVariant.SEAT,
  loadingSeats = false,
  setOnlyBuySeat = () => {},
  showTransferSeatsModal,
  generateUrlLinkData,
}) => {
  const { tt } = useGlobalTranslation()
  const location = useLocation()
  const currentPath = location.pathname

  const {
    user,
    accountID,
    selectedTeam,
    loadingChatCounter,
    hasPlatformRoles,
    selectedCampaignID,
  } = useSelector(state => ({
    user: state.user.profile,
    accountID: state.account.activeAccountID,
    selectedTeam: state.teamManagement.selectedTeam,
    loadingChatCounter: state.forms.formData,
    hasPlatformRoles: state.user.profile.hasPlatformRoles,
    selectedCampaignID: state.campaign.campaignDetails.id,
  }))
  const userID = user.id
  const teamID = selectedTeam?.teamId
  const rootUrl = `/users/${userID}/`

  const dispatch = useDispatch()
  const isSplitSearchVisible = useFlag("isSplitSearchVisible")

  const onChatClick = useCallback(() => {
    dispatch(
      appActions.updateFormField(
        "loadingChatCounter",
        typeof loadingChatCounter === "undefined" ? 0 : loadingChatCounter + 1,
      ),
    )
    dispatch(appActions.updateFormField("firstChatLoading", false))
  }, [dispatch, loadingChatCounter])

  const onLeadsClick = () => {
    dispatch(appActions.clearCampaignDetails())
    if (+selectedCampaignID && selectedCampaignID !== "all") {
      dispatch(
        appActions.getLeadsByCampaign("all", `?sortOrder=desc&sortColumn=createdAt&limit=50`),
      )
    }
  }

  const isExactMatch = (pattern, path) => {
    const cleanedPath = path.replace(/\/$/, "")

    const regexPattern = pattern.replace(/:\w+/g, "[^/]+").replace(/\//g, "\\/")

    const regex = new RegExp(`^${regexPattern}$`)
    return regex.test(cleanedPath)
  }

  const isActiveCapaignTab = isExactMatch(
    "/users/:userID/teams/:teamID/accounts/:accountID",
    currentPath,
  )

  const isLinkedinStreamFeatureEnabled = useFlag("LinkedinStreamFeature")
  const sidebarItems = useMemo(
    () => [
      {
        id: 3,
        linkTo: `/users/${userID}/teams/${teamID}/accounts/${accountID}`,
        icon: "speakerphone-28",
        isActive: isActiveCapaignTab,
        show: hasPermission(PERMISSIONS.CAMPAIGNS_VIEW),
        label: tt("campaigns"),
        tooltip: tt("campaigns"),
      },
      {
        id: 2,
        linkTo: `/users/${userID}/teams/${teamID}/accounts/${accountID}/selected-campaigns`,
        icon: "list-search-28",
        isActive: currentPath.includes("/selected-campaigns"),
        onClick: onLeadsClick,
        show: hasPermission(PERMISSIONS.CAMPAIGN_DETAIL_VIEW),
        label: tt("leads"),
        tooltip: tt("leads"),
      },
      {
        id: 3,
        linkTo: `/users/${userID}/teams/${teamID}/accounts/${accountID}/lead-lists`,
        icon: "list-search-28",
        isActive: currentPath.includes("/lead-lists"),
        onClick: onLeadsClick,
        show: isSplitSearchVisible,
        label: tt("lead-lists"),
        tooltip: tt("lead-lists"),
      },
      {
        id: 5,
        linkTo: `/users/${userID}/teams/${teamID}/accounts/${accountID}/chat/all`,
        icon: "message-2-28",
        isActive: currentPath.includes("/chat"),
        onClick: onChatClick,
        show: settings.featuresActive.includes("inbox") && hasPermission(PERMISSIONS.CHAT_VIEW),
        label: tt("messages"),
        tooltip: tt("messages"),
      },
      {
        id: 4,
        linkTo: `/users/${userID}/teams/${teamID}/accounts/${accountID}/statistics`,
        icon: "chart-histogram-28",
        isActive: currentPath.includes("/statistics"),
        show:
          settings.featuresActive.includes("analytics") &&
          hasPermission(PERMISSIONS.CAMPAIGN_REPORTS_VIEW),
        label: tt("reports"),
        tooltip: tt("reports"),
      },
      {
        id: 5,
        linkTo: `/users/${userID}/teams/${teamID}/accounts/${accountID}/blacklist`,
        icon: "user-cancel-28",
        isActive: currentPath.includes("/blacklist"),
        show:
          settings.featuresActive.includes("blacklist") &&
          hasPermission(PERMISSIONS.BLACKLIST_VIEW),
        label: tt("blacklist"),
        tooltip: tt("blacklist"),
      },
      {
        id: 6,
        linkTo: `/users/${userID}/teams/${teamID}/accounts/${accountID}/webhooks`,
        icon: "puzzle-28",
        isActive: currentPath.includes("/webhooks"),
        show:
          settings.featuresActive.includes("webhooks") && hasPermission(PERMISSIONS.WEBHOOKS_VIEW),
        label: tt("integrations"),
        tooltip: tt("integrations"),
      },
      /**
       * Global permission is necessary, for the case when user has no permission that allows them
       * viewing Settings page, but has Global permission for Blacklist, and hence should be able to access
       */
      {
        id: 7,
        linkTo: `/users/${userID}/teams/${teamID}/accounts/${accountID}/settings`,
        icon: "adjustments-horizontal-28",
        isActive:
          currentPath.includes("/settings") && !currentPath.includes("create-campaign/settings"),
        show:
          hasPermission(PERMISSIONS.SETTINGS_VIEW) ||
          (selectedTeam && selectedTeam.canManageGlobalBlacklist),
        label: tt("settings"),
        tooltip: tt("settings"),
      },
      {
        id: 9,
        linkTo: `/users/${userID}/teams/${teamID}/accounts/${accountID}/stream`,
        icon: "access-point-28",
        isActive: currentPath.includes("/stream"),
        show:
          isLinkedinStreamFeatureEnabled && hasPermission(PERMISSIONS.OPEN_LINKEDIN_PROFILE_VIEW),
        label: tt("linkedin-stream"),
        tooltip: tt("linkedin-stream"),
      },
    ],
    [
      rootUrl,
      userID,
      accountID,
      user.userTypeId,
      user.whitelabelId,
      settings.featuresActive,
      onChatClick,
      currentPath,
      isLinkedinStreamFeatureEnabled,
    ],
  )

  const teamSidebarItems = useMemo(
    () => [
      {
        id: 1,
        linkTo: `/users/${userID}/team-management`,
        icon: "user-star-28",
        isActive: currentPath === `/users/${userID}/team-management`,
        show: hasWhitelabelPermission(WHITELABEL_PERMISSIONS.TEAM_MANAGEMENT) && hasPlatformRoles,
        label: tt("platform-users"),
      },
      {
        id: 2,
        icon: "link-plus-28",
        show: isNotSkylead() && isAdmin() && !hideBilling() && secureRegistrationFeatureFlag,
        onClick: generateUrlLinkData?.onClick,
        label: tt("generate-registration-url"),
      },
      {
        id: 8,
        linkTo: location.pathname,
        onClick: () => window.open(`/users/${userID}/feature-requests`, "_blank"),
        icon: "rocket-28",
        isActive: currentPath.includes("/feature-requests"),
        show:
          ((isAdmin() && !hideBilling(user.id)) || user.whitelabelId === 1) &&
          hasWhitelabelPermission(WHITELABEL_PERMISSIONS.FEATURE_SUGGESTIONS),
        label: tt("feature-requests"),
      },
    ],
    [rootUrl, userID, accountID, user.userTypeId, user.whitelabelId, currentPath],
  )

  return (
    <Navigation
      navigationItems={navType === "seat" ? sidebarItems : teamSidebarItems}
      navbarWidth={navbarWidth}
      setNavbarWidth={setNavbarWidth}
      type={navType}
      loadingSeats={loadingSeats}
      setOnlyBuySeat={setOnlyBuySeat}
      showTransferSeatsModal={showTransferSeatsModal}
    />
  )
}

// Sidebar.propTypes = {
//   navbarWidth: PropTypes.number.isRequired,
//   setNavbarWidth: PropTypes.func,
//   navType: PropTypes.string,
//   loadingSeats: PropTypes.bool,
//   setOnlyBuySeat: PropTypes.func,
// }

export default React.memo(withRouter(Sidebar))
